import React from 'react';

import { AssetTypeTrendChart } from '@/shared/ui/AssetTypeTrendChart';
import { ChangeRateIndicator, type ChangeRateType } from '@/shared/ui/ChangeRateIndicator';

import styles from './AssetTypeStats.module.css';

export interface AssetTypeStat {
  type: string;
  name: string;
  count: number;
  change: number;
  changeRate: string;
  color: string;
  trend: number[];
  dates?: string[];
}

export interface AssetTypeStatsProps {
  stats: AssetTypeStat[];
  onAssetTypeClick: (assetType: string) => void;
}

const getChangeRateType = (changeRate: string): ChangeRateType => {
  const numericValue = parseFloat(changeRate.replace(/[^-+\d.]/g, ''));
  if (numericValue > 0) return 'positive';
  if (numericValue < 0) return 'negative';
  return 'neutral';
};

export const AssetTypeStats: React.FC<AssetTypeStatsProps> = React.memo(({ stats, onAssetTypeClick }) => {
  const handleKeyDown = (event: React.KeyboardEvent, assetType: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onAssetTypeClick(assetType);
    }
  };

  return (
    <div className={styles.assetTypeStats}>
      {stats.map((stat) => {
        const changeType = getChangeRateType(stat.changeRate);

        return (
          <div
            key={stat.type}
            className={styles.assetTypeItem}
            onClick={() => onAssetTypeClick(stat.type)}
            onKeyDown={(e) => handleKeyDown(e, stat.type)}
            tabIndex={0}
            role="button"
            aria-label={`${stat.name} - ${stat.count}개 자산, ${stat.changeRate} 변화`}>
            <div className={styles.labelRow}>
              <div className={styles.assetTypeColor} style={{ backgroundColor: stat.color }} />
              <div className={styles.assetTypeName}>{stat.name}</div>
            </div>
            <div className={styles.contentRow}>
              <div className={styles.leftSection}>
                <div className={styles.count}>{stat.count}</div>
                <ChangeRateIndicator
                  value={stat.changeRate}
                  showIcon={true}
                  animated={true}
                  className={styles.changeRate}
                />
              </div>
              <div className={styles.rightSection}>
                <AssetTypeTrendChart data={stat.trend} changeType={changeType} dates={stat.dates} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
});

AssetTypeStats.displayName = 'AssetTypeStats';
